import { generateWindowFromReelStrips } from '../../helpers';

export type Coin = { coinType: 'price'; multiplier: number } | { coinType: 'mini' | 'minor' | 'major' | 'grand' };
export type Cell = { reel: number; row: number };
export type Placement = Cell & ({ coin: Coin } | { collector: true });
export type BoostAction = Placement | (Cell & { upgrade: true });
export type Scenario = {
  reelIndexes?: number[]; window?: string[][]; coins?: Array<Cell & { coin: Coin }>;
  randomBonus?: 'activate' | 'skip'; wheel?: { wedgeIndex: number; boostActions?: BoostAction[] };
  respin?: Placement[];
};
export type Config = {
  reelsSetup: number[]; baseSpinsReels: string[][]; paytable: Record<string, number[]>;
  lines: number[][]; wild: string; maxWinInCreditsMultiplier: number;
  jackpotTotalBetMultipliers: Record<string, number>;
  coinFeature: { coins: Coin[]; triggerWeights: number[] };
  wheel: { weights: number[] };
};
export const wedges = ['major', 'extra-collect', 'mini', 'boost', 'extra-collect', 'minor',
  'boost', 'extra-collect', 'boost', 'grand', 'two-extra-collects', 'boost'];
export const symbols = ['wild', 'f2', 'f3', 'f4', 'f5', 'r1', 'r2', 'r3', 'r4'];
export const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const key = (cell: Cell) => `${cell.reel}:${cell.row}`;
export const coinId = (coin: Coin) => coin.coinType === 'price' ? String(coin.multiplier) : coin.coinType;
export const windowFor = (base: Scenario, config: Config) => base.window ?? generateWindowFromReelStrips({
  reelIndexes: base.reelIndexes ?? [0, 0, 0], reelStrips: config.baseSpinsReels, reels: 3, rows: 3,
});
export const cellsFor = (window: string[][], symbol: string) => window.flatMap((reel, index) =>
  reel.flatMap((value, row) => value === symbol ? [{ reel: index, row }] : []));
export const naturalTrigger = (window: string[][]) => window[0].includes('coin') && window[1].includes('collect') && window[2].includes('coin');
export const hasSpecial = (window: string[][]) => cellsFor(window, 'coin').length + cellsFor(window, 'collect').length > 0;
export const blank = (): Scenario[] => [{ reelIndexes: [0, 0, 0], randomBonus: 'skip' }];
export const forceCoins = (base: Scenario, config: Config): Scenario => ({ ...base,
  coins: cellsFor(windowFor(base, config), 'coin').map(cell => ({ ...cell,
    coin: base.coins?.find(value => key(value) === key(cell))?.coin ?? config.coinFeature.coins.find(coin => coin.coinType === 'price') ?? config.coinFeature.coins[0],
  })),
});
export const findTrigger = (config: Config, random = false): Scenario | undefined => {
  // Search one representative stop per special-symbol count on each reel.
  const stops = config.baseSpinsReels.map((strip, reel) => {
    const found = new Map<number, number>();
    strip.forEach((_, index) => {
      const count = [0, 1, 2].filter(row => strip[(index + row) % strip.length] === (reel === 1 ? 'collect' : 'coin')).length;
      if (!found.has(count)) found.set(count, index);
    });
    return Array.from(found.values());
  });
  for (const left of stops[0]) for (const center of stops[1]) for (const right of stops[2]) {
    const base = { reelIndexes: [left, center, right] };
    const window = windowFor(base, config);
    if (random ? hasSpecial(window) && !naturalTrigger(window) : naturalTrigger(window)) {
      return forceCoins({ ...base, randomBonus: random ? 'activate' : 'skip',
        wheel: { wedgeIndex: wedges.findIndex((prize, index) => prize === 'boost' && config.wheel.weights[index] > 0) } }, config);
    }
  }
};
/** Deterministic scenario defaults mirror the model; live play draws targets and prizes. */
export const projectBoost = (base: Scenario, config: Config) => {
  const window = windowFor(base, config);
  const defaultCoin = config.coinFeature.coins[config.coinFeature.triggerWeights.findIndex(weight => weight > 0)];
  const coins = cellsFor(window, 'coin').map(cell => ({ ...cell,
    coin: clone(base.coins?.find(placement => key(placement) === key(cell))?.coin ?? defaultCoin) }));
  const collectors = cellsFor(window, 'collect');
  if (base.randomBonus === 'activate' && !naturalTrigger(window)) {
    for (const reel of [0, 2]) if (!coins.some(coin => coin.reel === reel)) {
      coins.push({ reel, row: 0, coin: clone(defaultCoin) });
    }
    if (!collectors.length) collectors.push({ reel: 1, row: 0 });
  }
  const actions: BoostAction[] = [];
  const choices: BoostAction[][] = [];
  const value = (coin: Coin) => coin.coinType === 'price' ? coin.multiplier : config.jackpotTotalBetMultipliers[coin.coinType];
  const next = (coin: Coin): Coin | undefined => {
    if (['minor', 'major', 'grand'].includes(coin.coinType)) return undefined;
    const ladder: Coin[] = [...config.coinFeature.coins.filter(c => c.coinType === 'price'), { coinType: 'mini' }, { coinType: 'minor' }];
    return ladder.filter(c => value(c) > value(coin) && value(c) <= config.jackpotTotalBetMultipliers.minor)
      .sort((a, b) => value(a) - value(b) || (a.coinType === 'price' ? 1 : -1))[0];
  };
  for (let index = 0; index < 3; index++) {
    const free = [0, 2, 1].flatMap(reel => [0, 1, 2].map(row => ({ reel, row })))
      .filter(cell => ![...coins, ...collectors].some(occupied => key(occupied) === key(cell)));
    const options: BoostAction[] = free.length
      ? free.map(cell => cell.reel === 1
        ? { ...cell, collector: true } : { ...cell, coin: clone(defaultCoin) })
      : coins.filter(c => next(c.coin)).map(({ reel, row }) => ({ reel, row, upgrade: true }));
    if (!options.length) break;
    const action = clone(base.wheel?.boostActions?.[index] ?? options[0]);
    if (!options.some(option => key(option) === key(action) && ('coin' in option) === ('coin' in action) &&
      ('collector' in option) === ('collector' in action) && ('upgrade' in option) === ('upgrade' in action))) throw new Error('Invalid BOOST target.');
    const cell = { reel: action.reel, row: action.row };
    if ('coin' in action) {
      if (!config.coinFeature.coins.some(c => coinId(c) === coinId(action.coin))) throw new Error('BOOST coin is not configured.');
      coins.push({ ...cell, coin: clone(action.coin) });
    } else if ('collector' in action) collectors.push(cell);
    else {
      const selected = coins.find(c => key(c) === key(action))!;
      selected.coin = clone(next(selected.coin)!);
    }
    choices.push(options); actions.push(action);
  }
  if ((base.wheel?.boostActions?.length ?? 0) > actions.length) throw new Error('BOOST has no remaining legal action.');
  return { coins, collectors, actions, choices };
};
export type Projection = { collectors: Cell[]; respins: number; terminal: boolean; accumulatedWinInCredits: number; terminalReason?: 'max-win' | 'respins-exhausted' };
const baseLineWin = (window: string[][], config: Config) => config.lines.reduce((sum, line) => sum + Math.max(0, ...symbols.map(symbol =>
      line.every((row, reel) => window[reel][row] === symbol || window[reel][row] === config.wild)
        ? config.paytable[symbol]?.[0] ?? 0 : 0)), 0);
/** Preview only fully authored continuations; stop on exhaustion or the total-bet cap. */
export const project = (queue: Scenario[], config: Config): { steps: Projection[]; error?: string; scriptHint?: string } => {
  const steps: Projection[] = [];
  try {
    const base = queue[0];
    const window = windowFor(base, config);
    if (base.respin !== undefined) throw new Error('Start the queue with a base spin.');
    const coins = cellsFor(window, 'coin');
    const value = (coin: Coin) => coin.coinType === 'price' ? coin.multiplier : config.jackpotTotalBetMultipliers[coin.coinType];
    // One credit per line: evaluate only 3-of-a-kind, the model's sole paying length.
    let total = baseLineWin(window, config);
    const cap = config.maxWinInCreditsMultiplier * 5;
    if (total >= cap) {
      if (queue.length > 1) throw new Error('Paylines reach the win cap before the bonus; remove all respins.');
      return { steps: [], scriptHint: 'Paylines reach the win cap before the wheel or bonus.' };
    }
    const lineWin = total;
    let seedTotal = (base.coins ?? []).reduce((sum, placement) => sum + value(placement.coin), 0);
    const valuesPinned = coins.every(cell => base.coins?.some(placement => key(placement) === key(cell)));
    const collectors = cellsFor(window, 'collect');
    const triggered = naturalTrigger(window) || (base.randomBonus === 'activate' && hasSpecial(window));
    if (base.randomBonus === 'activate' && (!hasSpecial(window) || naturalTrigger(window))) {
      throw new Error('Random entry requires a Coin or Collect and an incomplete natural trigger.');
    }
    if (!triggered) {
      if (base.wheel || queue.length > 1) throw new Error('Wheel and respins require a bonus trigger.');
      return { steps };
    }
    const prize = base.wheel && wedges[base.wheel.wedgeIndex];
    if (base.randomBonus === 'activate' && !naturalTrigger(window)) {
      for (const reel of [0, 2]) if (!coins.some(cell => cell.reel === reel)) {
        coins.push({ reel, row: 0 });
        seedTotal += value(config.coinFeature.coins[config.coinFeature.triggerWeights.findIndex(weight => weight > 0)]);
      }
      if (!collectors.length) collectors.push({ reel: 1, row: 0 });
    }
    if (!prize) {
      if (queue.length > 1) throw new Error('Select a wheel prize before scripting respins.');
      return { steps, scriptHint: 'Select a wheel prize before scripting respins.' };
    }
    if (config.wheel.weights[base.wheel!.wedgeIndex] <= 0) throw new Error('This wheel prize is disabled in the selected config.');
    const extras = prize === 'extra-collect' ? 1 : prize === 'two-extra-collects' ? 2 : 0;
    if (collectors.length + extras > 3) throw new Error('The selected wheel prize needs more empty center cells.');
    for (let added = 0; added < extras; added++) collectors.push({ reel: 1, row: [0, 1, 2].find(row => !collectors.some(cell => cell.row === row))! });
    if (['mini', 'minor', 'major', 'grand'].includes(prize) && coins.length === 6) throw new Error('A wheel jackpot needs an empty outer cell.');
    if (!valuesPinned) {
      if (base.wheel?.boostActions?.length) throw new Error('Set all initial Coin values before scripting BOOST actions.');
      const scriptHint = 'Set all initial Coin values before scripting respins.';
      if (queue.length > 1) throw new Error(scriptHint);
      return { steps, scriptHint };
    }
    if (prize === 'boost') {
      const boost = projectBoost(base, config);
      collectors.splice(0, collectors.length, ...boost.collectors);
      seedTotal = boost.coins.reduce((sum, placement) => sum + value(placement.coin), 0);
    } else seedTotal += config.jackpotTotalBetMultipliers[prize] ?? 0;
    total += seedTotal * 5 * collectors.length;
    let respins = 3;
    let terminal = total >= cap;
    steps.push({ collectors: clone(collectors), respins, terminal, accumulatedWinInCredits: total - lineWin, ...(terminal ? { terminalReason: 'max-win' as const } : {}) });
    for (const scenario of queue.slice(1)) {
      if (terminal) throw new Error('Remove steps after the feature has ended.');
      if (!scenario.respin || Object.keys(scenario).some(field => field !== 'respin')) throw new Error('Bonus steps must contain only respin placements.');
      for (const placement of scenario.respin) if ('collector' in placement) {
        if (collectors.some(cell => key(cell) === key(placement))) throw new Error('A Collect is already held in that cell.');
        collectors.push({ reel: placement.reel, row: placement.row });
      }
      respins = scenario.respin.length ? 3 : respins - 1;
      total += scenario.respin.reduce((sum, placement) => sum + ('coin' in placement ? value(placement.coin) : 0), 0) * 5 * collectors.length;
      terminal = total >= cap || respins === 0;
      steps.push({ collectors: clone(collectors), respins, terminal, accumulatedWinInCredits: total - lineWin,
        ...(terminal ? { terminalReason: total >= cap ? 'max-win' as const : 'respins-exhausted' as const } : {}) });
    }
    return { steps };
  } catch (error) { return { steps, error: (error as Error).message }; }
};

// Refuse lossy editing of unknown presets. Their original JSON remains available.
export const isEditable = (queue: unknown, config: Config): queue is Scenario[] => {
  if (!Array.isArray(queue) || !queue.length || queue.length > 256) return false;
  const record = (value: any) => value && typeof value === 'object' && !Array.isArray(value);
  const validCoin = (coin: any) => record(coin) &&
    (coin.coinType !== 'price' || Number.isSafeInteger(coin.multiplier) && coin.multiplier > 0) && config.coinFeature.coins.some(c => coinId(c) === coinId(coin)) &&
    Object.keys(coin).every(k => ['coinType', ...(coin.coinType === 'price' ? ['multiplier'] : [])].includes(k));
  return queue.every((step: any, index) => {
    if (!record(step) || Object.keys(step).some(k => !(index ? ['respin'] : ['window', 'reelIndexes', 'coins', 'randomBonus', 'wheel']).includes(k))) return false;
    if (index && !Array.isArray(step.respin)) return false;
    if (!index) {
      if (step.window === undefined && step.reelIndexes === undefined) return false;
      if (step.window && step.reelIndexes) return false;
      if (step.window !== undefined && (!Array.isArray(step.window) || step.window.length !== 3 || step.window.some((reel: any, r: number) =>
        !Array.isArray(reel) || reel.length !== 3 || reel.some((s: string) => ![...symbols, r === 1 ? 'collect' : 'coin'].includes(s))))) return false;
      if (step.reelIndexes !== undefined && (!Array.isArray(step.reelIndexes) || step.reelIndexes.length !== 3 || step.reelIndexes.some((v: number, r: number) =>
        !Number.isSafeInteger(v) || v < 0 || v >= config.baseSpinsReels[r].length))) return false;
      if (step.randomBonus !== undefined && !['activate', 'skip'].includes(step.randomBonus)) return false;
      if (step.wheel !== undefined) {
        if (!record(step.wheel) || Object.keys(step.wheel).some(k => !['wedgeIndex', 'boostActions'].includes(k)) ||
          !Number.isSafeInteger(step.wheel.wedgeIndex) || !wedges[step.wheel.wedgeIndex]) return false;
        if (step.wheel.boostActions !== undefined && (wedges[step.wheel.wedgeIndex] !== 'boost' ||
          !Array.isArray(step.wheel.boostActions) || step.wheel.boostActions.length > 3 || !step.wheel.boostActions.every((a: any) =>
            record(a) && Number.isSafeInteger(a.reel) && a.reel >= 0 && a.reel < 3 && Number.isSafeInteger(a.row) && a.row >= 0 && a.row < 3 &&
            Object.keys(a).every(k => ['reel', 'row', 'coin', 'collector', 'upgrade'].includes(k)) &&
            ['coin', 'collector', 'upgrade'].filter(k => k in a).length === 1 &&
            (a.coin !== undefined ? a.reel !== 1 && validCoin(a.coin) : a.collector === true ? a.reel === 1 : a.upgrade === true && a.reel !== 1)))) return false;
      }
    }
    const placements = index ? step.respin : step.coins ?? [];
    return Array.isArray(placements) && placements.every(record) && new Set(placements.map(key)).size === placements.length && placements.every((p: any) =>
      record(p) && Number.isSafeInteger(p.reel) && p.reel >= 0 && p.reel < 3 && Number.isSafeInteger(p.row) && p.row >= 0 && p.row < 3 &&
      Object.keys(p).every(k => ['reel', 'row', 'coin', ...(index ? ['collector'] : [])].includes(k)) &&
      (p.coin !== undefined ? p.reel !== 1 && p.collector === undefined && validCoin(p.coin) &&
        (index || windowFor(step, config)[p.reel][p.row] === 'coin') : !!index && p.collector === true && p.reel === 1));
  });
};

/** The editor groups trigger and wheel controls; transport uses one scenario per API call. */
export const toPlayQueue = (queue: Scenario[], config: Config): Scenario[] => queue.flatMap(step => {
  if (!step.wheel || (!step.window && !step.reelIndexes)) return [clone(step)];
  const { wheel, ...trigger } = step;
  return baseLineWin(windowFor(trigger, config), config) >= config.maxWinInCreditsMultiplier * 5
    ? [clone(trigger)] : [clone(trigger), { wheel: clone(wheel) }];
});
export const fromPlayQueue = (queue: Scenario[]): Scenario[] => {
  const result: Scenario[] = [];
  for (const step of queue) {
    const previous = result[result.length - 1];
    if (step && Object.keys(step).length === 1 && step.wheel && previous && !previous.wheel && (previous.window || previous.reelIndexes)) {
      previous.wheel = clone(step.wheel);
    } else result.push(clone(step));
  }
  return result;
};
